import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close,
  ThumbUp,
  ThumbUpOutlined,
  PushPin,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useTogglePostLike } from '../../hooks/useCommunityMutations';
import type { CommunityPost, CommunityComment } from '../../types/community';
import { COLORS } from '@elevair/shared';
import CommentSection from './CommentSection';

interface PostDetailModalProps {
  open: boolean;
  onClose: () => void;
  post: CommunityPost | null;
  onReportComment?: (comment: CommunityComment) => void;
  onHideComment?: (commentId: string) => void;
  showAdminActions?: boolean;
  canWrite?: boolean;
}

export default function PostDetailModal({
  open,
  onClose,
  post,
  onReportComment,
  onHideComment,
  showAdminActions = false,
  canWrite = true,
}: PostDetailModalProps) {
  const toggleLike = useTogglePostLike();

  if (!post) return null;

  const handleLike = async () => {
    try {
      await toggleLike.mutateAsync(post._id);
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const getAvatarColor = (id: string) => {
    const colors = [COLORS.primary, COLORS.secondary, COLORS.accent, '#9C27B0', '#FF5722'];
    const index = id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center" gap={1.5} flex={1}>
            <Avatar
              sx={{
                bgcolor: post.author.avatar ? 'transparent' : getAvatarColor(post.author._id),
                width: 44,
                height: 44,
              }}
              src={post.author.avatar}
            >
              {post.author.firstName?.charAt(0)}
              {post.author.lastName?.charAt(0)}
            </Avatar>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {post.author.firstName} {post.author.lastName}
                </Typography>
                {post.author.tier && post.author.tier !== 'free' && (
                  <Chip
                    label={post.author.tier}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      textTransform: 'capitalize',
                      bgcolor:
                        post.author.tier === 'summit'
                          ? '#FEF3C7'
                          : post.author.tier === 'rise'
                          ? '#E5E7EB'
                          : '#FED7AA',
                      color:
                        post.author.tier === 'summit'
                          ? '#92400E'
                          : post.author.tier === 'rise'
                          ? '#374151'
                          : '#9A3412',
                    }}
                  />
                )}
                {post.isPinned && (
                  <Chip
                    icon={<PushPin sx={{ fontSize: 14 }} />}
                    label="Pinned"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: `${COLORS.accent}15`,
                      color: COLORS.accent,
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
                {post.updatedAt !== post.createdAt && ' (edited)'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Post Content */}
        <Box px={3} py={2}>
          {post.title && (
            <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
              {post.title}
            </Typography>
          )}

          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.7,
            }}
          >
            {post.content}
          </Typography>

          {/* Like Section */}
          <Box display="flex" alignItems="center" gap={2} mt={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: canWrite ? 'pointer' : 'default',
                color: post.isLiked ? COLORS.primary : 'text.secondary',
                py: 0.5,
                px: 1,
                borderRadius: 1,
                transition: 'background-color 0.2s',
                opacity: canWrite ? 1 : 0.7,
                '&:hover': canWrite ? {
                  color: COLORS.primary,
                  backgroundColor: `${COLORS.primary}10`,
                } : {},
              }}
              onClick={canWrite ? handleLike : undefined}
            >
              {post.isLiked ? (
                <ThumbUp sx={{ fontSize: 20 }} />
              ) : (
                <ThumbUpOutlined sx={{ fontSize: 20 }} />
              )}
              <Typography variant="body2" fontWeight={500}>
                {post.likesCount} {post.likesCount === 1 ? 'like' : 'likes'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Comments Section */}
        <Box px={3} py={2}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Comments ({post.commentsCount})
          </Typography>
          <CommentSection
            postId={post._id}
            onReportComment={onReportComment}
            onHideComment={onHideComment}
            showAdminActions={showAdminActions}
            onCommentPosted={onClose}
            canWrite={canWrite}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
